import * as THREE from 'three';
import { chamferBox, plainBox, tubeZ, rodZ, pipeZ, picatinnyRail, mergeGeom, place, translate, rotate } from './primitives.js';
import { ammoBelt } from './parts.js';
import { gripTexture } from './primitives.js';

/**
 * The roster: twenty-three weapons as data.
 *
 * Each spec is a description of a silhouette, not a list of boxes. The test every one
 * of these has to pass is the loadout screen: put the six assault rifles side by side
 * and you should be able to name each from its outline alone. That is why the specs
 * vary *topology* — where the magazine sits relative to the grip, whether the receiver
 * has a flat rail or a rounded cover, whether there is a pistol grip at all — and not
 * just lengths. A parameter sweep over one receiver shape produces six weapons that
 * are the same weapon.
 *
 * Sight height is the one number in every spec that has to be checked rather than
 * chosen. Everything forward of the optic — gas block, handguard, carry handle, muzzle
 * device, and above all the support hand — has to sit below the cone
 * `sightY - tan(cone) * dz`. The numbers below were solved against that and then
 * verified by raycasting the sight line through the built model.
 */

/* -------------------------------------------------------------------------- */
/* pose helpers                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Where the firing hand grabs a pistol grip.
 *
 * Derived from the grip's own geometry rather than typed in per weapon: the grip is
 * built around its top, rotated back by `rake`, then translated, so the point a
 * quarter of the way down its length lands here. Hand-tuned anchors were how the first
 * six weapons ended up with the trigger finger outside the guard on two of them.
 */
export function gripAnchor(g, frac = 0.25, x = 0.030) {
  const rake = g.rake ?? 0.44;
  const yl = -(g.h ?? 0.108) * frac;
  return [x, (g.y ?? -0.060) + yl * Math.cos(rake), (g.z ?? 0.066) - yl * Math.sin(rake) + 0.010];
}

/**
 * Where the support hand lies on a handguard.
 *
 * Always *under* the bore line, never beside it. `GRIPS.support` leans the knuckle row
 * forward for the same reason — a support hand at bore height is the bug that put a
 * glove in the middle of every sight picture in the game.
 */
export function supportAnchor(z, r = 0.022, drop = 0.004) {
  return [-(r + 0.014), -(r * 0.20 + drop), z];
}

/* -------------------------------------------------------------------------- */
/* per-weapon extras                                                           */
/* -------------------------------------------------------------------------- */

/** The A2 carry handle + rear aperture tower of a retro AR. */
/**
 * The A2 carry handle: two side walls and a bridge over an open channel.
 *
 * The first version filled the rear of the handle with a solid 30 x 30 x 32 mm block
 * "to house the aperture", centred on the sight axis. It was a plug — the same bug as
 * the marksman rifle's turret saddle and the holo sight's battery housing, for the
 * third time — and this weapon measured a 0.00-degree clear aperture because of it.
 * The aperture ring is built by the optic; the handle's job is only to hold it up, so
 * the channel between the walls stays open all the way through.
 */
function carryHandleTower(K, railY, z0, z1, top, halfWidth = 0.019) {
  const b = [];
  const len = z1 - z0;
  const h = top - railY - 0.006;
  for (const sx of [-1, 1]) {
    b.push(place(chamferBox(0.006, h, len, 0.0014), sx * halfWidth, railY + h / 2 + 0.003, (z0 + z1) / 2));
  }
  b.push(place(chamferBox(halfWidth * 2 + 0.006, 0.008, len, 0.0014), 0, top - 0.003, (z0 + z1) / 2));
  // Feet clamping the rail, and the forward web that stiffens the bridge.
  b.push(place(chamferBox(halfWidth * 2 + 0.006, 0.010, 0.026, 0.0014), 0, railY + 0.005, z0 + 0.014));
  b.push(place(chamferBox(halfWidth * 2 + 0.006, 0.010, 0.026, 0.0014), 0, railY + 0.005, z1 - 0.014));
  K.metal(mergeGeom(b));
}

/** A-frame front sight tower — the triangle that says "this is the old rifle". */
function frontSightTower(K, z, top, base = 0.006) {
  const b = [];
  b.push(place(chamferBox(0.026, 0.028, 0.028, 0.0018), 0, base + 0.008, z));
  for (const sx of [-1, 1]) {
    b.push(place(chamferBox(0.007, top - base - 0.012, 0.018, 0.0012), sx * 0.008, (top + base) / 2, z, 0, 0, sx * 0.16));
  }
  b.push(place(chamferBox(0.020, 0.008, 0.020, 0.0012), 0, top - 0.003, z));
  b.push(place(chamferBox(0.014, 0.012, 0.014, 0.0012), 0, base + 0.024, z + 0.020)); // sling swivel boss
  K.metal(mergeGeom(b));
}

/** Pump forend as an animated group, for the one weapon that has one. */
function pumpForend(K, z, len = 0.120) {
  const p = [];
  p.push(place(chamferBox(0.048, 0.046, len, 0.0060), 0, -0.020, 0));
  for (let i = -3; i <= 3; i++) p.push(place(plainBox(0.0055, 0.0055, len * 0.93), i * 0.0072, -0.0435, 0));
  p.push(place(chamferBox(0.052, 0.050, 0.012, 0.0040), 0, -0.020, len * 0.48));
  p.push(place(chamferBox(0.052, 0.050, 0.012, 0.0040), 0, -0.020, -len * 0.48));
  const pm = new THREE.Mesh(mergeGeom(p), K.M.poly);
  pm.frustumCulled = false;
  const pg = new THREE.Group();
  pg.add(pm);
  pg.position.set(0, 0, z);
  K.parts.pump = pg;
  return pg;
}

/** A loose shell held in the loading hand during shell-by-shell reloads. */
function loadShell(K) {
  const sg = new THREE.Group();
  sg.add(new THREE.Mesh(rodZ(0.0098, -0.030, 0.006, 12), K.M.wood));
  sg.add(new THREE.Mesh(rodZ(0.0104, 0.004, 0.014, 12), K.M.brass));
  sg.visible = false;
  sg.traverse((o) => (o.frustumCulled = false));
  K.parts.loadShell = sg;
  return sg;
}

/* -------------------------------------------------------------------------- */
/* ASSAULT RIFLES                                                              */
/* -------------------------------------------------------------------------- */

/**
 * AK-27 — stamped 7.62, wood furniture.
 *
 * The three cues that make an AK an AK, in order of how far away you can read them:
 * the deep banana magazine, the rounded dust cover with no flat rail on it, and the
 * stock raking *down* away from the bore rather than running straight back. The optic
 * sits high because an AK's dust cover is high; that is a feature, not a compromise.
 */
const ak27Grip = { w: 0.032, h: 0.096, d: 0.044, rake: 0.62, y: -0.046, z: 0.034, bucket: 'poly', swells: 2 };
export const ak27 = {
  id: 'ak27',
  cls: 'ar',
  ads: 0.139,
  receiver: { style: 'akBody', w: 0.038, len: 0.230, z: -0.020, y: -0.006, railLen: 0.120, railZ: 0.046 },
  lower: {
    z: 0.006, w: 0.036, h: 0.026, len: 0.086, y: -0.036,
    magwell: { w: 0.040, h: 0.028, d: 0.046, y: -0.052, z: -0.046 },
    controls: false,
    guard: { front: -0.024, back: 0.026, top: -0.050, bottom: -0.080, width: 0.0075 },
  },
  grip: ak27Grip,
  stock: { style: 'ak', z0: 0.100, len: 0.204, rake: 0.135, bucket: 'wood' },
  handguard: { style: 'ribbed', len: 0.150, z: -0.216, bucket: 'wood' },
  barrel: { steps: [[0.0094, -0.302, -0.138], [0.0082, -0.408, -0.302]] },
  gas: { z: -0.296, w: 0.026, h: 0.030, d: 0.034, y: 0.006, tube: false, regulator: true },
  muzzle: { style: 'slant', z: -0.408, r: 0.0128 },
  magazine: {
    style: 'curved', w: 0.0264, d: 0.0402, segH: 0.049, count: 4, curve: 0.118, chamfer: 0.0022,
    offset: [0, -0.062, -0.046], mat: 'metal',
  },
  charging: { style: 'side', side: 1, x: 0.026, y: 0.010, z: 0.022 },
  bolt: { w: 0.028, h: 0.024, len: 0.090, x: 0.002, y: 0.006, z: 0.010 },
  trigger: { y: -0.050, z: 0.004 },
  optic: { kind: 'reflex', y: 0.0620, z: 0.006, rTube: 0.0192, len: 0.058, reticle: 'dot' },
  ejectPoint: [0.024, 0.010, -0.014],
  ejectDir: [0.90, 0.32, 0.30],
  muzzleTip: -0.446,
  extras(K) {
    // Front sight block on the barrel, canted forward, plus the gas port elbow. An
    // AK's front sight is a separate block ahead of the gas block and it is what makes
    // the muzzle end look busy.
    // Heights here are solved against the sight cone, not chosen: at model z = -0.372
    // the 4.2-degree ray passes y = 0.0224, so the block tops out at 0.016 and the
    // protective wings at 0.018. A front sight that pokes into its own weapon's optic
    // is the carry-handle bug in miniature.
    K.metal(place(chamferBox(0.024, 0.028, 0.018, 0.0016), 0, 0.002, -0.372));
    for (const sx of [-1, 1]) K.metal(place(chamferBox(0.005, 0.016, 0.012, 0.0010), sx * 0.010, 0.010, -0.372));
    K.metal(place(chamferBox(0.014, 0.014, 0.030, 0.0012), 0, 0.012, -0.318, -0.35, 0, 0));
    K.metal(place(chamferBox(0.018, 0.014, 0.016, 0.0012), 0, -0.014, -0.318)); // cleaning-rod boss
  },
  hands: {
    right: { anchor: gripAnchor(ak27Grip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.040, -0.030, -0.216], curl: 0.95, spread: 0.32, grip: 'pump' },
  },
};

/**
 * X9 Maverick — the modern service carbine. Flat-top upper, slim M-LOK handguard,
 * collapsible stock on a buffer tube. The unbroken rail line from charging handle to
 * gas block is the thing to protect; anything that breaks it stops reading as an AR.
 */
const x9Grip = { w: 0.031, h: 0.108, d: 0.044, rake: 0.44, y: -0.060, z: 0.066, bucket: 'poly' };
export const x9maverick = {
  id: 'x9maverick',
  cls: 'ar',
  ads: 0.139,
  receiver: { style: 'arUpper', w: 0.040, h: 0.044, len: 0.226, z: -0.015, railLen: 0.196, railZ: 0.078, forwardAssist: true },
  lower: {
    z: 0.028, w: 0.034, h: 0.050, len: 0.120, y: -0.046,
    magwell: { w: 0.037, h: 0.064, d: 0.046, y: -0.048, z: -0.006 },
    guard: { front: 0.004, back: 0.062, top: -0.062, bottom: -0.092, width: 0.0075 },
  },
  grip: x9Grip,
  stock: { style: 'collapsible', z0: 0.096, z1: 0.250, y: -0.004, bodyZ: 0.196, bucket: 'poly' },
  barrelNut: { r: 0.0235, z: -0.126 },
  handguard: { style: 'mlok', len: 0.198, z: -0.231, r: 0.0212, slots: 5 },
  handguardRail: { len: 0.150, z: -0.132, y: 0.0215, teeth: 14 },
  barrel: { steps: [[0.0092, -0.352, -0.320], [0.0080, -0.418, -0.348]] },
  gas: { z: -0.338, w: 0.024, h: 0.026, d: 0.030, y: 0.003, tubeBack: -0.150, tubeY: 0.0125 },
  muzzle: { style: 'birdcage', z: -0.442, r: 0.0126, len: 0.028 },
  foregrip: { rake: 0.62, y: -0.030, z: -0.252, h: 0.062, w: 0.026 },
  magazine: {
    style: 'curved', w: 0.0248, d: 0.0355, segH: 0.052, count: 4, curve: 0.075, chamfer: 0.0022,
    offset: [0, -0.072, -0.006], mat: 'poly',
  },
  charging: { style: 'rear', y: 0.019, z: 0.086 },
  bolt: { w: 0.028, h: 0.024, len: 0.086, x: 0.004, y: 0.004, z: 0.012 },
  trigger: { y: -0.062, z: 0.040 },
  optic: { kind: 'reflex', y: 0.0620, z: -0.030, rTube: 0.0192, len: 0.056, reticle: 'dot' },
  sling: [[-0.0225, -0.012, -0.300]],
  ejectPoint: [0.024, 0.006, -0.002],
  ejectDir: [0.86, 0.34, 0.38],
  muzzleTip: -0.470,
  hands: {
    right: { anchor: gripAnchor(x9Grip), curl: 0.98, trigger: 0.75 },
    /**
     * On the angled foregrip, fist closed — not clamped over the handguard.
     *
     * The thumb-over handguard pose puts the knuckle row at model y = 0.0326 under a
     * sight axis at 0.0547, which clips this weapon's sight picture to 3.0 degrees.
     * That is the same bug that once measured 0.00, just less of it: a support hand
     * belongs under the line of sight, and the surest way to keep it there is to give
     * it something to hold that is already under the line of sight.
     */
    left: { anchor: [-0.030, -0.064, -0.250], curl: 1.00, spread: 0.18, grip: 'vertical' },
  },
};

/**
 * DS20 Mirage — bullpup.
 *
 * The magazine is behind the pistol grip and the receiver runs to the buttpad, so the
 * whole weapon is 20 cm shorter than the X9 for the same barrel. Nothing else in the
 * roster shares its outline, which is exactly why it is in it.
 */
const ds20Grip = { w: 0.032, h: 0.100, d: 0.042, rake: 0.30, y: -0.048, z: -0.086, bucket: 'grey' };
export const ds20mirage = {
  id: 'ds20mirage',
  cls: 'ar',
  ads: 0.132,
  receiver: { style: 'bullpup', w: 0.048, len: 0.400, z: 0.090, railY: 0.0345, railLen: 0.300, railZ: 0.250, bucket: 'grey' },
  lower: {
    z: -0.062, w: 0.034, h: 0.028, len: 0.070, y: -0.038, controls: false,
    guard: { front: -0.118, back: -0.052, top: -0.044, bottom: -0.074, width: 0.0075 },
  },
  grip: ds20Grip,
  stock: { style: 'bullpup', z: 0.294, y: -0.010, bucket: 'grey' },
  handguard: { style: 'mlok', len: 0.150, z: -0.170, r: 0.0206, slots: 4, bucket: 'grey' },
  barrel: { steps: [[0.0094, -0.330, -0.246], [0.0082, -0.396, -0.330]] },
  gas: { z: -0.286, w: 0.024, h: 0.024, d: 0.028, y: 0.002, tube: false },
  muzzle: { style: 'brake', z: -0.396, r: 0.0146, len: 0.048, bore: 0.0086, ports: 2, topPorts: true },
  foregrip: { rake: 0.0, y: -0.026, z: -0.196, h: 0.058, w: 0.028, flare: true, bucket: 'grey' },
  magazine: {
    style: 'curved', w: 0.0250, d: 0.0360, segH: 0.050, count: 4, curve: 0.070, chamfer: 0.0022,
    offset: [0, -0.036, 0.052], mat: 'grey',
  },
  charging: { style: 'side', side: -1, x: 0.026, y: 0.016, z: -0.056 },
  bolt: { w: 0.026, h: 0.022, len: 0.080, x: 0.002, y: 0.002, z: 0.130 },
  trigger: { y: -0.044, z: -0.096 },
  // A holographic window rather than a tube: the flattest, widest sight in the game,
  // which suits the weapon that is meant to feel fastest.
  optic: { kind: 'holo', y: 0.0655, z: -0.020, half: 0.0184, len: 0.070, reticle: 'circleDot', green: true },
  ejectPoint: [0.026, 0.004, 0.154],
  ejectDir: [0.92, 0.28, 0.26],
  muzzleTip: -0.444,
  extras(K) {
    // Magazine release paddle behind the well, and the rear sling point — the two
    // controls a bullpup puts somewhere no conventional rifle does.
    K.metal(place(chamferBox(0.030, 0.014, 0.016, 0.0014), 0, -0.030, 0.088));
    K.metal(place(chamferBox(0.020, 0.026, 0.010, 0.0012), 0, -0.020, 0.104));
  },
  hands: {
    right: { anchor: gripAnchor(ds20Grip), curl: 0.98, trigger: 0.76 },
    left: { anchor: [-0.032, -0.062, -0.196], curl: 1.02, spread: 0.20, grip: 'vertical' },
  },
};

/**
 * Peacekeeper MK1 — tan battle rifle on an extruded upper with a side-folding stock.
 * Squarer and heavier-looking than the X9, with a quad rail instead of M-LOK, and the
 * folded-out skeleton stock gives it an outline no other AR in the roster has.
 */
const pkGrip = { w: 0.033, h: 0.110, d: 0.046, rake: 0.42, y: -0.062, z: 0.068, bucket: 'tan' };
export const peacekeepermk1 = {
  id: 'peacekeepermk1',
  cls: 'ar',
  ads: 0.145,
  receiver: { style: 'extruded', w: 0.046, h: 0.048, len: 0.236, z: -0.022, railLen: 0.222, railZ: 0.088, bucket: 'tan' },
  lower: {
    z: 0.030, w: 0.036, h: 0.050, len: 0.124, y: -0.048,
    magwell: { w: 0.039, h: 0.066, d: 0.048, y: -0.050, z: -0.008 },
    guard: { front: 0.004, back: 0.064, top: -0.064, bottom: -0.094, width: 0.0078 },
  },
  grip: pkGrip,
  stock: { style: 'folding', z0: 0.104, len: 0.172, y: -0.008, cheek: true, bucket: 'tan' },
  handguard: { style: 'quad', len: 0.192, z: -0.222, r: 0.0224 },
  barrel: { steps: [[0.0104, -0.360, -0.320], [0.0090, -0.436, -0.356]] },
  gas: { z: -0.344, w: 0.026, h: 0.028, d: 0.034, y: 0.004, tubeBack: -0.146, tubeY: 0.0138, regulator: true },
  muzzle: { style: 'brake', z: -0.436, r: 0.0154, len: 0.054, bore: 0.0090, ports: 3 },
  foregrip: { rake: 0.10, y: -0.036, z: -0.246, h: 0.066, w: 0.028, bucket: 'tan' },
  magazine: {
    style: 'curved', w: 0.0268, d: 0.0388, segH: 0.052, count: 4, curve: 0.062, chamfer: 0.0024,
    offset: [0, -0.076, -0.008], mat: 'tan',
  },
  charging: { style: 'side', side: -1, x: 0.026, y: 0.012, z: 0.030 },
  bolt: { w: 0.030, h: 0.026, len: 0.092, x: 0.004, y: 0.004, z: 0.014 },
  trigger: { y: -0.064, z: 0.042 },
  // A 1-6x on a cantilever mount: the one AR in the roster with magnified glass.
  optic: {
    kind: 'lpvo', y: 0.0670, z: 0, rTube: 0.0212, rObj: 0.0276, rOc: 0.0226,
    zTip: -0.116, zBack: 0.094, ringZ: [-0.044, 0.040], turretZ: -0.028, reticle: 'bdc',
  },
  ejectPoint: [0.027, 0.006, 0.000],
  ejectDir: [0.88, 0.32, 0.34],
  muzzleTip: -0.490,
  hands: {
    right: { anchor: gripAnchor(pkGrip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.040, -0.026, -0.244], curl: 0.95, spread: 0.34 },
  },
};

/**
 * M15 MOD 0 — the retro carbine. Fixed tube stock, round ribbed handguard, A-frame
 * front sight tower and a carry handle with the rear aperture in it. It is the only
 * weapon in the roster whose sight line runs *through* its own carry handle, and that
 * high, narrow bridge over the receiver is what makes it unmistakable.
 */
const m15Grip = { w: 0.030, h: 0.100, d: 0.042, rake: 0.46, y: -0.056, z: 0.062, bucket: 'poly', swells: 2 };
export const m15mod0 = {
  id: 'm15mod0',
  cls: 'ar',
  ads: 0.150,
  receiver: {
    style: 'arUpper', w: 0.040, h: 0.044, len: 0.220, z: -0.014,
    railLen: 0.040, railZ: 0.090, railTeeth: 4, forwardAssist: true,
  },
  lower: {
    z: 0.026, w: 0.034, h: 0.050, len: 0.118, y: -0.046,
    magwell: { w: 0.037, h: 0.062, d: 0.044, y: -0.048, z: -0.006 },
    guard: { front: 0.002, back: 0.060, top: -0.062, bottom: -0.092, width: 0.0075 },
  },
  grip: m15Grip,
  stock: { style: 'fixedTube', z0: 0.094, len: 0.196, y: -0.006, bucket: 'poly' },
  handguard: { style: 'tube', len: 0.176, z: -0.220, w: 0.048, h: 0.046, y: -0.002, vents: 5, bucket: 'poly' },
  barrel: { steps: [[0.0086, -0.398, -0.310], [0.0076, -0.462, -0.398]] },
  muzzle: { style: 'birdcage', z: -0.464, r: 0.0124, len: 0.028 },
  magazine: {
    style: 'straight', w: 0.0244, d: 0.0350, len: 0.104, chamfer: 0.0022, ribs: 4,
    offset: [0, -0.076, -0.006], mat: 'metal',
  },
  charging: { style: 'rear', y: 0.019, z: 0.084 },
  bolt: { w: 0.028, h: 0.024, len: 0.086, x: 0.004, y: 0.004, z: 0.012 },
  trigger: { y: -0.062, z: 0.038 },
  optic: { kind: 'aperture', y: 0.0640, aperture: 0.0134, rearZ: 0.074, frontZ: -0.318, wing: 0.0112 },
  ejectPoint: [0.024, 0.006, -0.002],
  ejectDir: [0.86, 0.34, 0.38],
  muzzleTip: -0.494,
  extras(K) {
    // The carry handle the aperture lives in, and the A-frame tower it looks through.
    carryHandleTower(K, 0.0225, -0.010, 0.092, 0.0640 + 0.0290, 0.0240);
    frontSightTower(K, -0.318, 0.0640 + 0.0120, 0.008);
    // Delta ring and the barrel's under-slung sling swivel.
    K.metal(translate(tubeZ([[0.0268, -0.140], [0.0268, -0.126], [0.0232, -0.122]], 14), 0, -0.002, 0));
    K.metal(place(chamferBox(0.010, 0.018, 0.010, 0.0012), 0, -0.026, -0.300));
  },
  hands: {
    right: { anchor: gripAnchor(m15Grip), curl: 0.98, trigger: 0.75 },
    left: { anchor: [-0.040, -0.030, -0.226], curl: 0.96, spread: 0.30, grip: 'pump' },
  },
};

/**
 * MXR-17 — the angular one. Hexagonal handguard, skeleton stock with an adjustable
 * cheek riser, side-charging handle and a long ported brake. Same extruded receiver
 * family as the Peacekeeper, deliberately: they are meant to read as two rifles from
 * the same manufacturer, which is only convincing if something is actually shared.
 */
const mxrGrip = { w: 0.032, h: 0.106, d: 0.044, rake: 0.36, y: -0.058, z: 0.066, bucket: 'grey' };
export const mxr17 = {
  id: 'mxr17',
  cls: 'ar',
  ads: 0.136,
  receiver: { style: 'extruded', w: 0.048, h: 0.050, len: 0.232, z: -0.020, railLen: 0.218, railZ: 0.086, bucket: 'grey' },
  lower: {
    z: 0.028, w: 0.036, h: 0.048, len: 0.120, y: -0.048,
    magwell: { w: 0.039, h: 0.062, d: 0.046, y: -0.050, z: -0.008 },
    guard: { front: 0.004, back: 0.062, top: -0.064, bottom: -0.094, width: 0.0076 },
  },
  grip: mxrGrip,
  stock: { style: 'skeleton', z0: 0.112, len: 0.176, y: -0.004, cheek: true, bucket: 'grey' },
  handguard: { style: 'hex', len: 0.196, z: -0.226, r: 0.0262, y: 0.000, bucket: 'grey' },
  barrel: { steps: [[0.0100, -0.348, -0.330], [0.0088, -0.428, -0.348]] },
  gas: { z: -0.336, w: 0.024, h: 0.024, d: 0.030, y: 0.002, tube: false },
  muzzle: { style: 'brake', z: -0.428, r: 0.0160, len: 0.062, bore: 0.0092, ports: 3, topPorts: true },
  handStop: { w: 0.034, y: -0.036, z: -0.190, bucket: 'grey' },
  magazine: {
    style: 'straight', w: 0.0258, d: 0.0372, len: 0.116, chamfer: 0.0024, ribs: 4,
    offset: [0, -0.076, -0.008], mat: 'grey',
  },
  charging: { style: 'side', side: -1, x: 0.028, y: 0.014, z: 0.024 },
  bolt: { w: 0.030, h: 0.026, len: 0.090, x: 0.004, y: 0.004, z: 0.012 },
  trigger: { y: -0.064, z: 0.040 },
  optic: { kind: 'holo', y: 0.0640, z: -0.022, half: 0.0176, len: 0.066, reticle: 'chevron' },
  ejectPoint: [0.028, 0.006, 0.000],
  ejectDir: [0.88, 0.32, 0.34],
  muzzleTip: -0.490,
  extras(K) {
    // A short slab of rail continuing forward off the receiver. It stops at z = -0.230
    // and sits at y = 0.0150: the full-length version at 0.0272 put its teeth at
    // 0.0349, above the 5.1-degree ray at that distance, and clipped the sight picture
    // to 3.25 degrees.
    K.metal(translate(picatinnyRail(0.090, 0.0180, -0.140, 8), 0, 0.0150, 0));
    // Side lightening windows on the hex flats, which is where the look actually is.
    for (const sx of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        K.push('grey', place(chamferBox(0.005, 0.026, 0.014, 0.0012), sx * 0.0250, -0.006, -0.170 - i * 0.040));
      }
    }
  },
  hands: {
    right: { anchor: gripAnchor(mxrGrip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.042, -0.030, -0.240], curl: 0.95, spread: 0.34 },
  },
};

/* -------------------------------------------------------------------------- */
/* SUBMACHINE GUNS                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Razor 9mm — roller-locked 9 mm on a round stamped receiver. The cocking tube
 * standing off the left of the barrel shroud and the drum rear sight are the two
 * details that make this design readable, and both are cheap.
 */
const razorGrip = { w: 0.030, h: 0.098, d: 0.042, rake: 0.40, y: -0.048, z: 0.036, bucket: 'poly', swells: 2 };
export const razor9mm = {
  id: 'razor9mm',
  cls: 'smg',
  ads: 0.126,
  receiver: { style: 'smgTube', r: 0.0215, z0: -0.120, z1: 0.054, railLen: 0.090, railZ: 0.036, cockZ: -0.220 },
  lower: {
    z: 0.006, w: 0.032, h: 0.038, len: 0.118, y: -0.031,
    magwell: { w: 0.035, h: 0.056, d: 0.042, y: -0.036, z: -0.044 },
    controls: false,
    guard: { front: -0.024, back: 0.030, top: -0.048, bottom: -0.076, width: 0.0072 },
  },
  grip: razorGrip,
  stock: { style: 'wire', z0: 0.054, z1: 0.196, y: -0.010, dx: 0.019, bucket: 'poly' },
  handguard: { style: 'tube', len: 0.150, z: -0.196, w: 0.038, h: 0.036, y: -0.006, vents: 4, bucket: 'poly' },
  handguardRail: { len: 0.062, z: -0.192, y: -0.026 },
  barrel: { steps: [[0.0072, -0.268, -0.170]] },
  muzzle: { style: 'triLug', z: -0.290, r: 0.0105 },
  magazine: {
    style: 'curved', w: 0.0232, d: 0.0330, segH: 0.048, count: 4, curve: 0.085, chamfer: 0.0020,
    offset: [0, -0.058, -0.044], mat: 'metal',
  },
  charging: { style: 'tube', y: 0.014, z: -0.222 },
  bolt: { w: 0.026, h: 0.022, len: 0.074, x: 0.002, y: 0.005, z: 0.002 },
  trigger: { y: -0.048, z: 0.010 },
  optic: { kind: 'aperture', y: 0.0545, aperture: 0.0124, rearZ: 0.022, frontZ: -0.242, wing: 0.0108 },
  ejectPoint: [0.024, 0.008, -0.010],
  ejectDir: [0.90, 0.30, 0.30],
  muzzleTip: -0.312,
  hands: {
    right: { anchor: gripAnchor(razorGrip), curl: 0.98, trigger: 0.75 },
    left: { anchor: [-0.034, -0.028, -0.196], curl: 0.95, spread: 0.30 },
  },
};

/**
 * MPC-25 — polymer PDW. One moulded shell, a full-length top rail, a stubby folding
 * stock and a magazine that sits *ahead* of the trigger guard rather than under it.
 * Small, square and busy where the Razor is long and round.
 */
const mpcGrip = { w: 0.030, h: 0.092, d: 0.040, rake: 0.34, y: -0.044, z: 0.030, bucket: 'poly', swells: 2 };
export const mpc25 = {
  id: 'mpc25',
  cls: 'smg',
  ads: 0.126,
  receiver: { style: 'smgPoly', w: 0.042, h: 0.052, len: 0.204, z: -0.030, railLen: 0.180, railZ: 0.062, sideCharge: true },
  lower: {
    z: 0.004, w: 0.034, h: 0.030, len: 0.096, y: -0.034, controls: false,
    magwell: { w: 0.034, h: 0.042, d: 0.036, y: -0.040, z: -0.062 },
    guard: { front: -0.026, back: 0.026, top: -0.046, bottom: -0.074, width: 0.0072 },
  },
  grip: mpcGrip,
  stock: { style: 'folding', z0: 0.076, len: 0.116, y: -0.010, bucket: 'poly' },
  handguard: { style: 'mlok', len: 0.106, z: -0.190, r: 0.0180, slots: 3, t: 0.0050 },
  barrel: { steps: [[0.0072, -0.262, -0.140]] },
  muzzle: { style: 'flashCan', z: -0.262, r: 0.0142 },
  magazine: {
    style: 'straight', w: 0.0226, d: 0.0300, len: 0.098, chamfer: 0.0020, ribs: 3,
    offset: [0, -0.058, -0.062], mat: 'poly',
  },
  charging: { style: 'side', side: -1, x: 0.024, y: 0.016, z: 0.030 },
  bolt: { w: 0.026, h: 0.022, len: 0.070, x: 0.002, y: 0.004, z: 0.004 },
  trigger: { y: -0.046, z: 0.004 },
  optic: { kind: 'micro', y: 0.0580, z: -0.010, rTube: 0.0172, len: 0.042, reticle: 'dot' },
  ejectPoint: [0.023, 0.006, 0.012],
  ejectDir: [0.92, 0.30, 0.24],
  muzzleTip: -0.300,
  hands: {
    right: { anchor: gripAnchor(mpcGrip), curl: 0.98, trigger: 0.76 },
    left: { anchor: [-0.032, -0.032, -0.192], curl: 0.96, spread: 0.28 },
  },
};

/**
 * RK-9 — magazine through the pistol grip.
 *
 * That one decision moves the magazine 90 mm back from where every other weapon in the
 * roster puts it, gives the weapon a completely empty space under the barrel, and is
 * the cheapest unmistakable silhouette in the whole project. Everything else is a
 * stamped box and a wire stock.
 */
const rk9Grip = { w: 0.038, h: 0.104, d: 0.048, rake: 0.16, y: -0.036, z: 0.010, bucket: 'poly', swells: 2 };
export const rk9 = {
  id: 'rk9',
  cls: 'smg',
  ads: 0.128,
  receiver: { style: 'stampedBox', w: 0.044, h: 0.052, len: 0.176, z: -0.036, railLen: 0.086, railZ: 0.030 },
  lower: {
    z: 0.010, w: 0.036, h: 0.020, len: 0.070, y: -0.030, controls: false,
    guard: { front: -0.016, back: 0.036, top: -0.042, bottom: -0.070, width: 0.0074 },
  },
  grip: rk9Grip,
  stock: { style: 'wire', z0: 0.052, z1: 0.170, y: -0.006, dx: 0.021, bucket: 'poly' },
  handguard: { style: 'none' },
  barrel: { steps: [[0.0088, -0.208, -0.124]] },
  muzzle: { style: 'thread', z: -0.208, r: 0.0098 },
  magazine: {
    style: 'straight', w: 0.0250, d: 0.0320, len: 0.120, chamfer: 0.0022, ribs: 4,
    // Inside the grip, which is the entire point of the design.
    offset: [0, -0.052, 0.014], mat: 'metal',
  },
  charging: { style: 'top', y: 0.032, z: -0.010 },
  bolt: { w: 0.030, h: 0.024, len: 0.078, x: 0.002, y: 0.006, z: -0.010 },
  trigger: { y: -0.040, z: 0.006 },
  optic: { kind: 'micro', y: 0.0625, z: 0.010, rTube: 0.0174, len: 0.042, reticle: 'dot' },
  ejectPoint: [0.023, 0.010, -0.008],
  ejectDir: [0.92, 0.30, 0.26],
  muzzleTip: -0.226,
  extras(K) {
    // Barrel nut and the grip safety at the rear of the front strap — the two details
    // that stop a stamped box reading as a shoebox.
    K.metal(translate(tubeZ([[0.0150, -0.128], [0.0150, -0.116], [0.0122, -0.112]], 12), 0, 0, 0));
    K.metal(place(chamferBox(0.028, 0.050, 0.008, 0.0018), 0, -0.062, -0.014, -0.16, 0, 0));
    // The support hand has nowhere to go on a weapon this short but the magazine, so
    // there is a proper finger shelf moulded onto the front of the grip housing.
    K.poly(place(chamferBox(0.042, 0.014, 0.030, 0.0030), 0, -0.030, -0.030));
  },
  hands: {
    right: { anchor: gripAnchor(rk9Grip, 0.20), curl: 0.98, trigger: 0.74 },
    // Wrapped over the magazine housing, well below the bore.
    left: { anchor: [-0.030, -0.086, 0.006], curl: 1.00, spread: 0.16, grip: 'vertical' },
  },
};

/**
 * Dravec 45 — tall angular .45. The receiver stands much deeper than any other subgun
 * here and the magazine rakes forward out of the front of it, so the outline is a
 * wedge rather than a tube. Long shroud, folding stock, red dot.
 */
const dravecGrip = { w: 0.032, h: 0.096, d: 0.044, rake: 0.22, y: -0.050, z: 0.028, bucket: 'grey', swells: 2 };
export const dravec45 = {
  id: 'dravec45',
  cls: 'smg',
  ads: 0.130,
  receiver: { style: 'smgPoly', w: 0.044, h: 0.070, len: 0.200, z: -0.024, railLen: 0.186, railZ: 0.066, bucket: 'grey' },
  lower: {
    z: 0.004, w: 0.036, h: 0.030, len: 0.100, y: -0.040, controls: false,
    guard: { front: -0.026, back: 0.030, top: -0.052, bottom: -0.082, width: 0.0074 },
  },
  grip: dravecGrip,
  stock: { style: 'folding', z0: 0.084, len: 0.132, y: -0.012, bucket: 'grey' },
  handguard: { style: 'tube', len: 0.128, z: -0.190, w: 0.040, h: 0.034, y: -0.008, vents: 3, bucket: 'grey' },
  barrel: { steps: [[0.0078, -0.286, -0.128]] },
  muzzle: { style: 'flashCan', z: -0.286, r: 0.0150 },
  foregrip: { rake: 0.0, y: -0.028, z: -0.186, h: 0.052, w: 0.026, flare: true, bucket: 'grey' },
  magazine: {
    style: 'straight', w: 0.0268, d: 0.0330, len: 0.138, chamfer: 0.0024, ribs: 5,
    offset: [0, -0.062, -0.078], mat: 'grey',
  },
  charging: { style: 'side', side: -1, x: 0.026, y: 0.024, z: 0.030 },
  bolt: { w: 0.028, h: 0.026, len: 0.082, x: 0.002, y: 0.010, z: 0.006 },
  trigger: { y: -0.052, z: 0.004 },
  optic: { kind: 'reflex', y: 0.0690, z: -0.006, rTube: 0.0186, len: 0.052, reticle: 'dotRing' },
  ejectPoint: [0.024, 0.014, 0.008],
  ejectDir: [0.92, 0.28, 0.26],
  muzzleTip: -0.324,
  extras(K) {
    // The forward-raked magazine housing: a sloped shroud around the well, which is
    // what gives this weapon its wedge.
    K.push('grey', place(chamferBox(0.040, 0.062, 0.030, 0.0035), 0, -0.052, -0.076, 0.26, 0, 0));
  },
  hands: {
    right: { anchor: gripAnchor(dravecGrip), curl: 0.98, trigger: 0.75 },
    left: { anchor: [-0.030, -0.066, -0.186], curl: 1.00, spread: 0.18, grip: 'vertical' },
  },
};

/**
 * Ryden 45K — the retro one. Round receiver with the bolt handle on top, finned
 * barrel, wooden furniture, a vertical foregrip and a drum magazine. Nothing about it
 * is modern and every part of it is a silhouette cue.
 */
const rydenGrip = { w: 0.034, h: 0.098, d: 0.046, rake: 0.30, y: -0.056, z: 0.048, bucket: 'woodLight', swells: 2 };
export const ryden45k = {
  id: 'ryden45k',
  cls: 'smg',
  ads: 0.134,
  receiver: { style: 'retroTube', r: 0.0230, z0: -0.130, z1: 0.070, topRail: true },
  lower: {
    z: 0.018, w: 0.034, h: 0.030, len: 0.096, y: -0.038, controls: false,
    guard: { front: -0.008, back: 0.044, top: -0.050, bottom: -0.080, width: 0.0078 },
  },
  grip: rydenGrip,
  /**
   * Dropped 16 mm below where it started.
   *
   * At y = -0.014 the comb topped out 30 mm under a sight axis at 50 mm, and since the
   * buttstock sits barely 7 cm from the eye at full ADS, that put a slab of pale wood
   * across the bottom 40% of the sight picture — photographed, and by far the worst
   * sight picture in the roster. A low comb is also what the real thing has: an open
   * notch is aimed along the top of the receiver, not over the stock.
   */
  stock: { style: 'wood', z0: 0.062, len: 0.206, y: -0.030, drop: 0.08, bucket: 'woodLight' },
  handguard: { style: 'none' },
  barrel: {
    steps: [[0.0098, -0.300, -0.132]],
    fins: { count: 9, r: 0.0148, z0: -0.290, z1: -0.150, thickness: 0.0055 },
  },
  muzzle: { style: 'compensator', z: -0.300, r: 0.0150 },
  foregrip: { rake: 0.0, y: -0.026, z: -0.208, h: 0.078, w: 0.030, flare: true, bucket: 'woodLight' },
  magazine: {
    style: 'drum', r: 0.055, w: 0.030, neck: { w: 0.026, d: 0.034, h: 0.026 },
    offset: [0, -0.050, -0.030], mat: 'metal',
  },
  charging: { style: 'top', y: 0.030, z: 0.020 },
  bolt: { w: 0.030, h: 0.026, len: 0.086, x: 0.000, y: 0.004, z: 0.010 },
  trigger: { y: -0.050, z: 0.020 },
  optic: { kind: 'notch', y: 0.0500, rearZ: 0.056, frontZ: -0.292, ear: 0.0104 },
  ejectPoint: [0.024, 0.006, -0.032],
  ejectDir: [0.92, 0.30, 0.24],
  muzzleTip: -0.342,
  extras(K) {
    // Barrel band and the front sight blade on top of the compensator.
    K.metal(place(chamferBox(0.036, 0.034, 0.014, 0.0018), 0, 0, -0.134));
    K.metal(place(chamferBox(0.020, 0.012, 0.018, 0.0014), 0, 0.022, -0.292));
  },
  hands: {
    right: { anchor: gripAnchor(rydenGrip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.032, -0.060, -0.208], curl: 1.00, spread: 0.18, grip: 'vertical' },
  },
};

/**
 * Carbon 57 — top-loading bullpup.
 *
 * The magazine lies flat along the top of the receiver and the whole weapon is one
 * sweeping polymer shell with a thumbhole cut through the front. It is the least
 * gun-shaped object in the game and it should stay that way; every attempt to add
 * conventional furniture to this layout makes it worse.
 */
export const carbon57 = {
  id: 'carbon57',
  cls: 'smg',
  ads: 0.128,
  receiver: { style: 'p90Shell', w: 0.052, len: 0.330, z: 0.056, railY: 0.0560 },
  lower: null,
  grip: null,
  stock: { style: 'none' },
  handguard: { style: 'none' },
  barrel: { steps: [[0.0080, -0.150, -0.030]] },
  muzzle: { style: 'flashCan', z: -0.150, r: 0.0136 },
  magazine: {
    style: 'horizontal', len: 0.210, w: 0.036, h: 0.024,
    offset: [0, 0.042, 0.030], mat: 'grey',
  },
  charging: null,
  bolt: { w: 0.026, h: 0.020, len: 0.072, x: 0.002, y: -0.004, z: 0.060 },
  trigger: { y: -0.046, z: -0.058 },
  optic: { kind: 'micro', y: 0.0830, z: -0.062, rTube: 0.0168, len: 0.044, reticle: 'dot' },
  ejectPoint: [0.006, -0.048, -0.020],
  ejectDir: [0.10, -0.92, 0.20],
  muzzleTip: -0.188,
  extras(K) {
    // Trigger housing and guard, moulded into the shell rather than bolted under it.
    K.poly(place(chamferBox(0.040, 0.056, 0.052, 0.0040), 0, -0.048, -0.062));
    K.metal(translate(
      // The guard is a full loop here — a bullpup's trigger hangs in free air.
      mergeGeom([
        place(chamferBox(0.008, 0.030, 0.008, 0.0012), 0, -0.062, -0.090, 0.30, 0, 0),
        place(chamferBox(0.008, 0.008, 0.048, 0.0012), 0, -0.078, -0.064),
        place(chamferBox(0.008, 0.026, 0.008, 0.0012), 0, -0.062, -0.038, -0.30, 0, 0),
      ]), 0, 0, 0));
    // Optic riser: a short pillar up out of the deck, since the magazine occupies the
    // spine and there is nowhere else for glass to go.
    K.metal(place(chamferBox(0.030, 0.024, 0.058, 0.0018), 0, 0.052, -0.062));
    // Emissive charge strip down the left flank, the one flourish this weapon gets.
    K.push('accent', place(plainBox(0.003, 0.006, 0.090), -0.0262, -0.014, 0.020));
    // Rail teeth along the deck either side of the magazine, and the moulded rib that
    // ties them together. A shell this smooth needs somewhere for the eye to land.
    for (const sx of [-1, 1]) {
      K.metal(translate(picatinnyRail(0.120, 0.0110, -0.010, 9), sx * 0.0248, 0.0300, 0));
    }
    // Thumbhole surround: the hole is the grip, so its rim gets a raised bead all the
    // way round rather than being a plain cut through the shell.
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      K.poly(place(chamferBox(0.036, 0.008, 0.008, 0.0012), 0, -0.062 + Math.sin(a) * 0.026, -0.062 + Math.cos(a) * 0.024));
    }
    // Sling loops fore and aft, and the ambidextrous cocking handles.
    K.metal(place(chamferBox(0.010, 0.018, 0.010, 0.0012), 0.0270, 0.010, -0.086));
    K.metal(place(chamferBox(0.010, 0.018, 0.010, 0.0012), -0.0270, 0.010, -0.086));
    for (const sx of [-1, 1]) {
      K.metal(place(chamferBox(0.012, 0.014, 0.026, 0.0014), sx * 0.0288, 0.014, 0.010));
    }
    // Cooling slots down the barrel shroud under the deck.
    for (let i = 0; i < 5; i++) {
      K.poly(place(chamferBox(0.030, 0.008, 0.010, 0.0012), 0, -0.028, -0.100 - i * 0.020));
    }
  },
  hands: {
    // The firing hand grips the moulded thumbhole rather than a separate grip.
    right: { anchor: [0.030, -0.070, -0.044], curl: 0.98, trigger: 0.76 },
    left: { anchor: [-0.030, -0.086, -0.126], curl: 1.00, spread: 0.16, grip: 'vertical' },
  },
};

/* -------------------------------------------------------------------------- */
/* SHOTGUNS                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * M10 Breacher — short pump with a heat shield and a toothed standoff muzzle. The
 * forend is an animated part, and the support hand is parented to it so it rides the
 * pump stroke for free.
 */
const m10Grip = { w: 0.034, h: 0.098, d: 0.046, rake: 0.34, y: -0.052, z: 0.086, bucket: 'poly' };
export const m10breacher = {
  id: 'm10breacher',
  cls: 'shotgun',
  ads: 0.126,
  receiver: { style: 'pumpReceiver', w: 0.044, h: 0.058, len: 0.180, z: 0.020, y: -0.014, railLen: 0.104, railZ: 0.074 },
  lower: null,
  grip: m10Grip,
  stock: { style: 'folding', z0: 0.116, len: 0.128, y: -0.030, bucket: 'poly' },
  handguard: { style: 'shroud', z0: -0.286, z1: -0.170, r: 0.0166, rings: 3 },
  barrel: { steps: [[0.0128, -0.300, -0.078]] },
  muzzle: { style: 'breacher', z: -0.300, r: 0.0175 },
  magazine: null,
  charging: null,
  bolt: false,
  trigger: { y: -0.046, z: 0.056, h: 0.028, w: 0.0075 },
  optic: { kind: 'ghost', y: 0.0600, aperture: 0.0128, rearZ: 0.056, frontZ: -0.292 },
  ejectPoint: [0.026, -0.008, 0.004],
  ejectDir: [0.92, 0.24, 0.30],
  muzzleTip: -0.352,
  extras(K) {
    K.metal(translate(triggerGuardLoop(0.024, 0.090, -0.044, -0.076), 0, 0, 0));
    K.metal(translate(tubeMag(0.0125, -0.286, -0.072), 0, -0.0268, 0));
    K.metal(place(chamferBox(0.024, 0.030, 0.020, 0.0016), 0, -0.014, -0.240)); // barrel clamp
    pumpForend(K, -0.140, 0.104);
    loadShell(K);
    // The elevator visible through the loading port.
    const c = new THREE.Mesh(mergeGeom([place(chamferBox(0.030, 0.008, 0.056, 0.0012), 0, 0, 0)]), K.M.dark);
    c.position.set(0, -0.036, -0.012);
    c.frustumCulled = false;
    K.parts.carrier = c;
  },
  hands: {
    right: { anchor: gripAnchor(m10Grip), curl: 0.98, trigger: 0.68 },
    left: { anchor: [-0.039, -0.032, 0.0], curl: 0.95, spread: 0.30, parent: 'pump', grip: 'pump' },
  },
};

/**
 * Echo 12 — box-fed semi-auto shotgun on a stamped AK-pattern receiver. A ten-round
 * 12-gauge box is enormous, and hanging one under an AK body produces a silhouette
 * that is instantly readable and shared with nothing else in the game.
 */
const echoGrip = { w: 0.033, h: 0.098, d: 0.046, rake: 0.56, y: -0.048, z: 0.034, bucket: 'poly', swells: 2 };
export const echo12 = {
  id: 'echo12',
  cls: 'shotgun',
  ads: 0.130,
  receiver: { style: 'akBody', w: 0.044, len: 0.220, z: -0.014, y: -0.008, railLen: 0.116, railZ: 0.048 },
  lower: {
    z: 0.008, w: 0.040, h: 0.024, len: 0.084, y: -0.040, controls: false,
    magwell: { w: 0.052, h: 0.030, d: 0.062, y: -0.056, z: -0.048 },
    guard: { front: -0.022, back: 0.028, top: -0.052, bottom: -0.082, width: 0.0078 },
  },
  grip: echoGrip,
  stock: { style: 'skeleton', z0: 0.104, len: 0.158, y: -0.014, cheek: false, bucket: 'poly' },
  handguard: { style: 'tube', len: 0.140, z: -0.208, w: 0.048, h: 0.042, y: -0.008, vents: 4, bucket: 'poly' },
  handguardRail: { len: 0.070, z: -0.180, y: -0.034 },
  barrel: { steps: [[0.0126, -0.324, -0.134]] },
  muzzle: { style: 'brake', z: -0.324, r: 0.0176, len: 0.044, bore: 0.0114, ports: 2, topPorts: true },
  magazine: {
    style: 'curved', w: 0.0430, d: 0.0500, segH: 0.040, count: 3, curve: 0.055, chamfer: 0.0026,
    offset: [0, -0.070, -0.048], mat: 'poly',
  },
  charging: { style: 'side', side: 1, x: 0.030, y: 0.012, z: 0.020 },
  bolt: { w: 0.032, h: 0.028, len: 0.092, x: 0.002, y: 0.004, z: 0.010 },
  trigger: { y: -0.052, z: 0.004 },
  optic: { kind: 'reflex', y: 0.0650, z: 0.004, rTube: 0.0196, len: 0.056, reticle: 'dotRing' },
  ejectPoint: [0.026, 0.010, -0.010],
  ejectDir: [0.90, 0.32, 0.28],
  muzzleTip: -0.368,
  extras(K) {
    K.metal(place(chamferBox(0.030, 0.032, 0.020, 0.0016), 0, 0.004, -0.290)); // gas block
    K.metal(place(chamferBox(0.018, 0.014, 0.016, 0.0012), 0.020, 0.006, -0.290));
  },
  hands: {
    right: { anchor: gripAnchor(echoGrip), curl: 0.98, trigger: 0.72 },
    left: { anchor: [-0.040, -0.032, -0.208], curl: 0.95, spread: 0.30, grip: 'pump' },
  },
};

/**
 * Akita — sawn-off side-by-side.
 *
 * Two barrels, a hinge, a top lever and a wooden wrist with no pistol grip. It is half
 * the length of anything else in the game and has no receiver above the bore at all,
 * so from the side it reads as a pair of tubes and a stick. Aim is over a rib with a
 * single bead: no rim, no ring, nothing in the way.
 */
export const akita = {
  id: 'akita',
  cls: 'shotgun',
  ads: 0.126,
  receiver: { style: 'breakAction', z: 0.030, spacing: 0.0148 },
  lower: null,
  grip: { style: 'wrist', y: -0.024, z: 0.070, bucket: 'wood' },
  stock: { style: 'wood', z0: 0.062, len: 0.176, y: -0.030, drop: 0.06, bucket: 'wood' },
  handguard: { style: 'none' },
  barrel: null,
  muzzle: null,
  magazine: null,
  charging: null,
  bolt: false,
  trigger: { y: -0.040, z: 0.004, h: 0.030 },
  optic: { kind: 'bead', y: 0.0330, frontZ: -0.276, rearZ: -0.010, ribLen: 0.250 },
  ejectPoint: [0.020, 0.012, -0.010],
  ejectDir: [0.70, 0.62, 0.34],
  muzzleTip: -0.286,
  muzzleY: 0.0148,
  extras(K) {
    const sp = 0.0148;
    // Two barrels side by side, joined by a top rib and a fore-end lug.
    for (const sx of [-1, 1]) {
      K.blued(translate(rodZ(0.0138, -0.286, -0.014, 12), sx * sp, 0.0148, 0));
      K.blued(translate(tubeZ([[0.0150, -0.286], [0.0150, -0.276], [0.0138, -0.272]], 12), sx * sp, 0.0148, 0));
    }
    // Side ribs filling the notch between the tubes, so it reads as one assembly.
    K.blued(place(chamferBox(sp * 2, 0.010, 0.264, 0.0012), 0, 0.0148 + 0.0100, -0.150));
    K.blued(place(chamferBox(sp * 2, 0.010, 0.264, 0.0012), 0, 0.0148 - 0.0100, -0.150));
    // Wooden splinter fore-end under the barrels — where the support hand goes.
    K.push('wood', place(chamferBox(0.042, 0.032, 0.104, 0.0050), 0, -0.008, -0.128));
    K.push('wood', place(chamferBox(0.038, 0.020, 0.030, 0.0035), 0, -0.020, -0.070));
    K.metal(place(chamferBox(0.020, 0.012, 0.016, 0.0014), 0, -0.022, -0.176)); // fore-end latch
    // Double trigger: the second blade behind the first.
    K.metal(place(chamferBox(0.0075, 0.026, 0.009, 0.0012), 0, -0.050, 0.020));
    K.metal(translate(triggerGuardLoop(-0.010, 0.036, -0.036, -0.064), 0, 0, 0));
    loadShell(K);
    // Checkering on the wrist and the fore-end. This weapon has the fewest parts of
    // anything in the roster — two tubes and a stick — so the triangles it saves on
    // structure go into the surfaces the hands actually touch, which is also where a
    // real sporting gun puts its craftsmanship.
    K.push('wood', translate(gripTexture(0.028, 0.050, 4, 7, 0.0038, 0.0010), 0, -0.046, 0.070));
    K.push('wood', translate(gripTexture(0.028, 0.044, 4, 6, 0.0038, 0.0010), 0, -0.026, -0.078));
    for (const sx of [-1, 1]) {
      K.push('wood', translate(gripTexture(0.020, 0.046, 3, 6, 0.0036, 0.0009), sx * 0.021, -0.008, -0.128));
    }
    // Engraved scroll panels on the action flats, as raised beads.
    for (const sx of [-1, 1]) {
      for (let i = 0; i < 4; i++) {
        K.metal(place(plainBox(0.0022, 0.0060, 0.0060), sx * 0.0262, -0.006 - i * 0.010, 0.030));
      }
    }
    // Extractor plate and the two firing-pin bosses on the standing breech.
    K.metal(place(chamferBox(0.038, 0.010, 0.006, 0.0010), 0, 0.0148, -0.012));
    for (const sx of [-1, 1]) K.metal(place(chamferBox(0.008, 0.008, 0.005, 0.0008), sx * 0.0148, 0.0148, -0.011));
  },
  hands: {
    right: { anchor: [0.030, -0.048, 0.068], curl: 0.96, trigger: 0.66 },
    left: { anchor: [-0.034, -0.026, -0.128], curl: 0.98, spread: 0.24, grip: 'pump' },
  },
};

/* -------------------------------------------------------------------------- */
/* LIGHT MACHINE GUNS                                                          */
/* -------------------------------------------------------------------------- */

/**
 * XM325 — belt-fed SAW. Feed tray cover, box under the receiver, bipod out, and a
 * carry handle on the barrel because barrel changes are why it exists.
 *
 * Sight height is 70 mm, the tallest in the game, and it has to be: the optic sits on
 * a feed tray cover which is itself on a receiver deep enough to pass a belt through.
 * The carry handle is the tightest clearance on the weapon and is capped accordingly.
 */
const xmGrip = { w: 0.033, h: 0.112, d: 0.046, rake: 0.42, y: -0.064, z: 0.082, bucket: 'poly' };
export const xm325 = {
  id: 'xm325',
  cls: 'lmg',
  ads: 0.132,
  receiver: { style: 'beltFed', w: 0.052, len: 0.300, z: -0.040, railY: 0.0375, railLen: 0.190, railZ: 0.020, railTeeth: 14 },
  lower: {
    z: 0.042, w: 0.040, h: 0.042, len: 0.128, y: -0.050, controls: false,
    guard: { front: 0.010, back: 0.072, top: -0.066, bottom: -0.098, width: 0.0082 },
  },
  grip: xmGrip,
  stock: { style: 'skeleton', z0: 0.126, len: 0.176, y: -0.008, cheek: false, bucket: 'poly' },
  handguard: { style: 'mlok', len: 0.150, z: -0.284, r: 0.0232, slots: 4, y: -0.002 },
  barrelNut: { r: 0.029, z: -0.182 },
  barrel: {
    steps: [[0.0150, -0.400, -0.196], [0.0132, -0.520, -0.400]],
    flutes: { count: 4, r: 0.0150, z: -0.300, len: 0.150, size: 0.006 },
  },
  gas: { z: -0.352, w: 0.030, h: 0.032, d: 0.036, y: 0.003, tubeR: 0.0040, tubeBack: -0.196, tubeY: 0.0165, regulator: true },
  muzzle: { style: 'birdcage', z: -0.550, r: 0.0155, len: 0.034 },
  foregrip: { rake: 0.58, y: -0.036, z: -0.276, h: 0.066, w: 0.028 },
  carryHandle: { z: -0.300, len: 0.086, top: 0.0235 },
  bipod: { z: -0.470, len: 0.185 },
  magazine: {
    style: 'beltBox', w: 0.086, h: 0.104, d: 0.146, offset: [0, -0.086, -0.048], mat: 'poly',
    belt: { count: 6, from: [-0.030, 0.010, -0.020], to: [-0.026, 0.070, -0.052], sag: 0.010 },
  },
  charging: { style: 'side', side: 1, x: 0.030, y: 0.006, z: 0.010 },
  bolt: { w: 0.032, h: 0.028, len: 0.104, x: 0.003, y: -0.002, z: 0.010 },
  trigger: { y: -0.066, z: 0.050 },
  optic: { kind: 'reflex', y: 0.0760, z: -0.032, rTube: 0.0192, len: 0.058, reticle: 'dotRing' },
  ejectPoint: [0.028, -0.004, -0.028],
  ejectDir: [0.88, 0.30, 0.36],
  muzzleTip: -0.596,
  extras(K) {
    // The short run of belt still standing in the feed tray once the box is gone.
    K.dark(translate(ammoBelt(2, [-0.022, -0.018, -0.096], [-0.008, -0.004, -0.100], 0.002), 0, 0, 0));
  },
  hands: {
    right: { anchor: gripAnchor(xmGrip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.038, -0.030, -0.278], curl: 0.96, spread: 0.32 },
  },
};

/**
 * MK.78 — drum-fed squad rifle on a lengthened stamped receiver.
 *
 * The counterpart to the XM325 and deliberately built the other way round: no belt, no
 * feed tray, no carry handle — a 75-round drum, a very long finned barrel and a
 * classic club-footed stock. Two LMGs that share nothing but a bipod.
 */
const mk78Grip = { w: 0.033, h: 0.100, d: 0.046, rake: 0.58, y: -0.048, z: 0.036, bucket: 'wood' };
export const mk78 = {
  id: 'mk78',
  cls: 'lmg',
  ads: 0.140,
  receiver: { style: 'akBody', w: 0.042, len: 0.256, z: -0.020, y: -0.008, railLen: 0.130, railZ: 0.056 },
  lower: {
    z: 0.008, w: 0.040, h: 0.026, len: 0.090, y: -0.040, controls: false,
    magwell: { w: 0.044, h: 0.028, d: 0.050, y: -0.056, z: -0.048 },
    guard: { front: -0.024, back: 0.028, top: -0.052, bottom: -0.084, width: 0.0080 },
  },
  grip: mk78Grip,
  stock: { style: 'ak', z0: 0.116, len: 0.216, rake: 0.115, bucket: 'wood' },
  handguard: { style: 'ribbed', len: 0.176, z: -0.250, bucket: 'wood' },
  barrel: {
    steps: [[0.0116, -0.360, -0.152], [0.0100, -0.500, -0.360]],
    fins: { count: 7, r: 0.0166, z0: -0.348, z1: -0.212, thickness: 0.006 },
  },
  gas: { z: -0.336, w: 0.028, h: 0.028, d: 0.034, y: 0.002, tube: false, regulator: true },
  muzzle: { style: 'brake', z: -0.500, r: 0.0156, len: 0.048, bore: 0.0090, ports: 2 },
  bipod: { z: -0.470, len: 0.190 },
  magazine: {
    style: 'drum', r: 0.062, w: 0.034, neck: { w: 0.028, d: 0.038, h: 0.030 },
    offset: [0, -0.058, -0.048], mat: 'metal',
  },
  charging: { style: 'side', side: 1, x: 0.028, y: 0.012, z: 0.024 },
  bolt: { w: 0.030, h: 0.026, len: 0.098, x: 0.002, y: 0.006, z: 0.012 },
  trigger: { y: -0.052, z: 0.004 },
  optic: { kind: 'reflex', y: 0.0780, z: 0.008, rTube: 0.0192, len: 0.058, reticle: 'dot' },
  ejectPoint: [0.026, 0.010, -0.014],
  ejectDir: [0.90, 0.32, 0.30],
  muzzleTip: -0.548,
  extras(K) {
    // Front sight block and the gas elbow, as on the AK-27 but pushed much further out.
    // Capped against the sight cone, as on the AK-27: at z = -0.424 the 4.6-degree
    // ray passes y = 0.0170, so nothing here goes above it.
    K.metal(place(chamferBox(0.026, 0.026, 0.020, 0.0016), 0, 0.000, -0.424));
    for (const sx of [-1, 1]) K.metal(place(chamferBox(0.005, 0.016, 0.014, 0.0010), sx * 0.011, 0.008, -0.424));
    K.metal(place(chamferBox(0.016, 0.016, 0.032, 0.0012), 0, 0.008, -0.362, -0.35, 0, 0));
  },
  hands: {
    right: { anchor: gripAnchor(mk78Grip), curl: 0.98, trigger: 0.74 },
    left: { anchor: [-0.040, -0.032, -0.250], curl: 0.95, spread: 0.32, grip: 'pump' },
  },
};

/* -------------------------------------------------------------------------- */
/* MARKSMAN RIFLES                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Warden 308 — large-frame AR marksman rifle. Everything about the X9 stretched: a
 * longer upper, a 256 mm handguard, a heavy fluted barrel and a proper scope, but the
 * same unbroken flat-top line so it reads as a member of the same family.
 */
const wardenGrip = { w: 0.033, h: 0.112, d: 0.046, rake: 0.42, y: -0.066, z: 0.072, bucket: 'poly' };
export const warden308 = {
  id: 'warden308',
  cls: 'dmr',
  ads: 0.150,
  receiver: { style: 'arUpper', w: 0.045, h: 0.050, len: 0.252, z: -0.024, railLen: 0.244, railZ: 0.088, railTeeth: 13, forwardAssist: true },
  lower: {
    z: 0.032, w: 0.038, h: 0.054, len: 0.132, y: -0.050,
    magwell: { w: 0.041, h: 0.070, d: 0.052, y: -0.052, z: -0.010 },
    guard: { front: 0.002, back: 0.068, top: -0.068, bottom: -0.100, width: 0.0080 },
  },
  grip: wardenGrip,
  stock: { style: 'skeleton', z0: 0.112, len: 0.190, y: -0.004, cheek: true, bucket: 'poly' },
  barrelNut: { r: 0.0265, z: -0.150 },
  handguard: { style: 'mlok', len: 0.256, z: -0.282, r: 0.0238, slots: 4, t: 0.006 },
  handguardRail: { len: 0.170, z: -0.154, y: 0.0250, teeth: 12 },
  barrel: {
    steps: [[0.0125, -0.470, -0.408], [0.0105, -0.520, -0.470]],
    flutes: { count: 6, r: 0.0125, z: -0.440, len: 0.056 },
  },
  muzzle: { style: 'brake', z: -0.520, r: 0.0155, len: 0.070, bore: 0.0092, ports: 3 },
  magazine: {
    style: 'curved', w: 0.0272, d: 0.0400, segH: 0.050, count: 4, curve: 0.055, chamfer: 0.0024,
    offset: [0, -0.080, -0.010], mat: 'metal',
  },
  charging: { style: 'rear', y: 0.021, z: 0.096 },
  bolt: { w: 0.032, h: 0.028, len: 0.098, x: 0.004, y: 0.005, z: 0.014 },
  trigger: { y: -0.068, z: 0.044 },
  optic: {
    kind: 'lpvo', y: 0.0700, z: 0, rTube: 0.0218, rObj: 0.0282, rOc: 0.0230,
    zTip: -0.120, zBack: 0.096, ringZ: [-0.052, 0.030], turretZ: -0.030, reticle: 'milDot',
  },
  ejectPoint: [0.027, 0.008, -0.006],
  ejectDir: [0.88, 0.36, 0.30],
  muzzleTip: -0.590,
  extras(K) {
    // Bipod stub folded back along the handguard, plus the under-rail it clamps to.
    K.metal(translate(picatinnyRail(0.090, 0.0180, -0.320), 0, -0.0330, 0));
    for (const sx of [-1, 1]) K.metal(place(chamferBox(0.012, 0.010, 0.078, 0.0012), sx * 0.010, -0.040, -0.360, 0.10, 0, 0));
  },
  hands: {
    right: { anchor: gripAnchor(wardenGrip), curl: 0.98, trigger: 0.70 },
    left: { anchor: [-0.039, -0.032, -0.282], curl: 0.95, spread: 0.35 },
  },
};

/**
 * M8A1 — the wooden battle rifle. A milled action bedded in a full-length walnut
 * stock, no pistol grip, no rail, and the scope on a side mount because the action is
 * open-topped. Nothing else in the game has this outline and nothing else should.
 */
export const m8a1 = {
  id: 'm8a1',
  cls: 'dmr',
  ads: 0.150,
  receiver: { style: 'classicRifle', w: 0.040, len: 0.230, z: -0.010, railY: 0.0480 },
  lower: {
    z: 0.020, w: 0.038, h: 0.024, len: 0.082, y: -0.036, controls: false,
    magwell: { w: 0.040, h: 0.026, d: 0.048, y: -0.050, z: -0.030 },
    guard: { front: -0.006, back: 0.048, top: -0.048, bottom: -0.078, width: 0.0080 },
  },
  grip: { style: 'wrist', y: -0.026, z: 0.088, bucket: 'wood' },
  stock: { style: 'wood', z0: 0.108, len: 0.250, y: -0.030, drop: 0.07, bucket: 'wood' },
  handguard: { style: 'wood', len: 0.244, z: -0.247, bucket: 'wood' },
  barrel: { steps: [[0.0112, -0.404, -0.132], [0.0098, -0.492, -0.404]] },
  gas: { z: -0.388, w: 0.026, h: 0.026, d: 0.030, y: 0.004, tube: false },
  muzzle: { style: 'birdcage', z: -0.492, r: 0.0134, len: 0.030 },
  magazine: {
    style: 'curved', w: 0.0272, d: 0.0400, segH: 0.046, count: 4, curve: 0.048, chamfer: 0.0024,
    offset: [0, -0.062, -0.030], mat: 'blued',
  },
  charging: { style: 'side', side: 1, x: 0.026, y: 0.012, z: 0.030 },
  bolt: { w: 0.030, h: 0.024, len: 0.094, x: 0.002, y: 0.008, z: 0.012 },
  trigger: { y: -0.048, z: 0.020 },
  optic: {
    kind: 'lpvo', y: 0.0810, z: 0, rTube: 0.0218, rObj: 0.0282, rOc: 0.0230,
    zTip: -0.116, zBack: 0.098, ringZ: [-0.050, 0.034], turretZ: -0.028, reticle: 'bdc',
  },
  ejectPoint: [0.024, 0.014, -0.014],
  ejectDir: [0.86, 0.44, 0.26],
  muzzleTip: -0.522,
  extras(K) {
    // Front sight blade and the muzzle's bayonet lug — period details that cost eighty
    // triangles and do most of the work of dating this weapon.
    K.metal(place(chamferBox(0.018, 0.020, 0.016, 0.0014), 0, 0.014, -0.470));
    K.metal(place(chamferBox(0.012, 0.014, 0.030, 0.0012), 0, -0.018, -0.452));
    K.metal(place(chamferBox(0.030, 0.010, 0.024, 0.0014), 0, -0.008, -0.386)); // gas cylinder lock
  },
  hands: {
    right: { anchor: [0.030, -0.062, 0.086], curl: 0.96, trigger: 0.70 },
    left: { anchor: [-0.038, -0.030, -0.256], curl: 0.95, spread: 0.30, grip: 'pump' },
  },
};

/**
 * M34 Novaline — bullpup marksman rifle. Long barrel in a short package, big glass on
 * a full-length rail, angled foregrip and a bipod. Shares the DS20's shell family for
 * the same reason the Peacekeeper and MXR share theirs.
 */
const novaGrip = { w: 0.033, h: 0.104, d: 0.044, rake: 0.26, y: -0.050, z: -0.090, bucket: 'grey' };
export const m34novaline = {
  id: 'm34novaline',
  cls: 'dmr',
  ads: 0.150,
  receiver: { style: 'bullpup', w: 0.050, len: 0.430, z: 0.098, railY: 0.0360, railLen: 0.330, railZ: 0.270, bucket: 'grey' },
  lower: {
    z: -0.066, w: 0.036, h: 0.028, len: 0.072, y: -0.040, controls: false,
    guard: { front: -0.122, back: -0.056, top: -0.046, bottom: -0.078, width: 0.0078 },
  },
  grip: novaGrip,
  stock: { style: 'bullpup', z: 0.310, y: -0.010, bucket: 'grey' },
  handguard: { style: 'mlok', len: 0.170, z: -0.212, r: 0.0212, slots: 5, bucket: 'grey' },
  barrel: { steps: [[0.0116, -0.400, -0.300], [0.0102, -0.486, -0.400]] },
  muzzle: { style: 'brake', z: -0.486, r: 0.0158, len: 0.058, bore: 0.0092, ports: 3 },
  foregrip: { rake: 0.60, y: -0.032, z: -0.240, h: 0.062, w: 0.028, bucket: 'grey' },
  bipod: { z: -0.252, len: 0.150 },
  magazine: {
    style: 'curved', w: 0.0272, d: 0.0400, segH: 0.048, count: 4, curve: 0.050, chamfer: 0.0024,
    offset: [0, -0.038, 0.056], mat: 'grey',
  },
  charging: { style: 'side', side: -1, x: 0.028, y: 0.016, z: -0.060 },
  bolt: { w: 0.028, h: 0.024, len: 0.086, x: 0.002, y: 0.002, z: 0.136 },
  trigger: { y: -0.046, z: -0.100 },
  optic: {
    kind: 'lpvo', y: 0.0700, z: 0, rTube: 0.0218, rObj: 0.0284, rOc: 0.0232,
    zTip: -0.110, zBack: 0.100, ringZ: [-0.044, 0.036], turretZ: -0.026, reticle: 'milDot',
  },
  ejectPoint: [0.027, 0.004, 0.160],
  ejectDir: [0.92, 0.28, 0.26],
  muzzleTip: -0.544,
  hands: {
    right: { anchor: gripAnchor(novaGrip), curl: 0.98, trigger: 0.72 },
    left: { anchor: [-0.032, -0.066, -0.240], curl: 1.00, spread: 0.20, grip: 'vertical' },
  },
};

/* -------------------------------------------------------------------------- */
/* SNIPER RIFLES                                                               */
/* -------------------------------------------------------------------------- */

/**
 * VS Recon — semi-automatic designated sniper rifle, thumbhole stock, side-rail scope.
 * The open thumbhole and the long slim slotted handguard are the silhouette; it is the
 * only sniper in the roster that is not bolt-worked and it should not look like one.
 */
export const vsrecon = {
  id: 'vsrecon',
  cls: 'sniper',
  ads: 0.146,
  receiver: { style: 'akBody', w: 0.040, len: 0.250, z: -0.026, y: -0.004, sideRail: true, topRail: false },
  lower: {
    z: 0.006, w: 0.038, h: 0.026, len: 0.090, y: -0.038, controls: false,
    magwell: { w: 0.042, h: 0.030, d: 0.052, y: -0.054, z: -0.052 },
    guard: { front: -0.028, back: 0.030, top: -0.052, bottom: -0.084, width: 0.0080 },
  },
  grip: null,
  stock: { style: 'thumbhole', z0: 0.104, len: 0.232, bucket: 'wood' },
  handguard: { style: 'ribbed', len: 0.170, z: -0.240, bucket: 'wood' },
  barrel: { steps: [[0.0102, -0.400, -0.156], [0.0092, -0.520, -0.400]] },
  gas: { z: -0.330, w: 0.026, h: 0.028, d: 0.030, y: 0.006, tube: false },
  muzzle: { style: 'brake', z: -0.520, r: 0.0148, len: 0.052, bore: 0.0086, ports: 2, topPorts: true },
  magazine: {
    style: 'curved', w: 0.0272, d: 0.0420, segH: 0.048, count: 4, curve: 0.070, chamfer: 0.0024,
    offset: [0, -0.062, -0.052], mat: 'metal',
  },
  charging: { style: 'side', side: 1, x: 0.028, y: 0.014, z: 0.026 },
  bolt: { w: 0.028, h: 0.024, len: 0.094, x: 0.002, y: 0.008, z: 0.012 },
  trigger: { y: -0.052, z: 0.002 },
  optic: {
    kind: 'scope', y: 0.0740, z: 0, rTube: 0.0246, rObj: 0.0316, rOc: 0.0252,
    zTip: -0.142, zBack: 0.098, ringZ: [-0.062, 0.032], turretZ: -0.030, reticle: 'christmasTree',
  },
  ejectPoint: [0.026, 0.012, -0.016],
  ejectDir: [0.90, 0.34, 0.26],
  muzzleTip: -0.572,
  extras(K) {
    // Side mount arm bridging from the receiver rail to under the scope tube — the
    // detail that makes an off-centre mount read as deliberate rather than broken.
    // The bridge stops at the bottom of the scope tube rather than crossing under it:
    // at y = 0.062 it was a chord across the bore and clipped this rifle to 1.25
    // degrees — the scope-ring bug in a hand-built mount.
    K.metal(place(chamferBox(0.012, 0.040, 0.062, 0.0018), -0.024, 0.030, 0.008));
    K.metal(place(chamferBox(0.036, 0.012, 0.066, 0.0016), -0.010, 0.0444, 0.008));
    // Front sight block and the gas elbow.
    K.metal(place(chamferBox(0.024, 0.026, 0.018, 0.0016), 0, 0.002, -0.442));
    K.metal(place(chamferBox(0.016, 0.016, 0.030, 0.0012), 0, 0.010, -0.352, -0.35, 0, 0));
  },
  hands: {
    // No pistol grip: the firing hand takes the thumbhole's front web.
    right: { anchor: [0.030, -0.068, 0.086], curl: 0.98, trigger: 0.70 },
    left: { anchor: [-0.040, -0.032, -0.240], curl: 0.95, spread: 0.30, grip: 'pump' },
  },
};

/**
 * Shadow SK — suppressed bolt-action on a folding chassis.
 *
 * The bolt is a rotating group rather than a sliding one: `rotation.z` lifts the
 * handle and `position.z` runs it back, which is the whole cycle and the one thing
 * that separates this weapon from everything else in the game mechanically.
 */
const shadowGrip = { w: 0.034, h: 0.116, d: 0.048, rake: 0.26, y: -0.070, z: 0.086, bucket: 'grey' };
export const shadowsk = {
  id: 'shadowsk',
  cls: 'sniper',
  ads: 0.150,
  receiver: { style: 'boltChassis', w: 0.044, h: 0.054, len: 0.310, z: -0.020, railLen: 0.300, railZ: 0.120, railTeeth: 15 },
  lower: {
    z: 0.040, w: 0.040, h: 0.046, len: 0.150, y: -0.048,
    magwell: { w: 0.044, h: 0.070, d: 0.056, y: -0.050, z: -0.048 },
    controls: false,
    guard: { front: 0.004, back: 0.070, top: -0.068, bottom: -0.102, width: 0.0082 },
  },
  grip: shadowGrip,
  stock: { style: 'folding', z0: 0.128, len: 0.184, y: -0.014, cheek: true, bucket: 'grey' },
  handguard: { style: 'mlok', len: 0.190, z: -0.296, r: 0.0224, slots: 4, y: -0.002, bucket: 'grey' },
  barrelNut: { r: 0.027, z: -0.176 },
  barrel: {
    steps: [[0.0148, -0.430, -0.186]],
    flutes: { count: 6, r: 0.0148, z: -0.390, len: 0.150, size: 0.0060 },
  },
  muzzle: { style: 'suppressor', z: -0.430, r: 0.0200, len: 0.158 },
  bipod: { z: -0.372, len: 0.200 },
  magazine: {
    style: 'curved', w: 0.0288, d: 0.0430, segH: 0.038, count: 3, curve: 0.030, chamfer: 0.0026,
    offset: [0, -0.082, -0.048], mat: 'metal',
  },
  charging: null,
  bolt: false,
  trigger: { y: -0.070, z: 0.046, h: 0.030 },
  optic: {
    kind: 'scope', y: 0.0820, z: 0, rTube: 0.0250, rObj: 0.0324, rOc: 0.0258,
    zTip: -0.152, zBack: 0.101, ringZ: [-0.062, 0.038], turretZ: -0.030, reticle: 'christmasTree',
  },
  ejectPoint: [0.026, 0.010, -0.014],
  ejectDir: [0.90, 0.34, 0.26],
  muzzleTip: -0.588,
  extras(K) {
    K.metal(translate(picatinnyRail(0.086, 0.0180, -0.320), 0, -0.0320, 0));
    /**
     * The bolt, as a group whose pivot is the bore axis, so `rotation.z` lifts the
     * handle and `position.z` runs it back. Nothing else in the game needs a rotating
     * part; a bolt gun is defined by one.
     */
    const g = new THREE.Group();
    const body = new THREE.Mesh(
      mergeGeom([
        translate(rodZ(0.0104, -0.020, 0.150, 12), 0, 0, 0),
        place(chamferBox(0.024, 0.024, 0.020, 0.0016), 0, 0, 0.150),
        place(chamferBox(0.014, 0.014, 0.012, 0.0012), 0, 0, 0.166),
      ]),
      K.M.dark
    );
    body.position.y = 0.012;
    g.add(body);
    const handle = new THREE.Mesh(
      mergeGeom([
        place(chamferBox(0.042, 0.011, 0.012, 0.0014), 0.028, 0, 0.086),
        place(chamferBox(0.014, 0.014, 0.014, 0.0022), 0.050, 0, 0.086),
      ]),
      K.M.metal
    );
    handle.position.y = 0.012;
    g.add(handle);
    g.traverse((o) => (o.frustumCulled = false));
    K.parts.bolt = g;
    K.parts.boltIsGroup = true;
  },
  hands: {
    right: { anchor: gripAnchor(shadowGrip), curl: 0.98, trigger: 0.68 },
    left: { anchor: [-0.038, -0.026, -0.296], curl: 0.95, spread: 0.34 },
  },
};

/**
 * XR-3 Ion — bullpup anti-materiel rifle with a coil accelerator muzzle.
 *
 * The heaviest, longest thing in the game: a huge scope, a stacked-ring muzzle on
 * standoff rails, and the only emissive surfaces on any weapon. It is the one place
 * the art brief's "stylised military hardware" is allowed to lean toward science
 * fiction, and it earns that by still being built like a rifle underneath.
 */
const ionGrip = { w: 0.034, h: 0.108, d: 0.046, rake: 0.24, y: -0.054, z: -0.094, bucket: 'grey' };
export const xr3ion = {
  id: 'xr3ion',
  cls: 'sniper',
  ads: 0.150,
  receiver: { style: 'bullpup', w: 0.054, len: 0.440, z: 0.104, railY: 0.0380, railLen: 0.340, railZ: 0.280, bucket: 'grey' },
  lower: {
    z: -0.070, w: 0.038, h: 0.030, len: 0.076, y: -0.042, controls: false,
    guard: { front: -0.126, back: -0.060, top: -0.048, bottom: -0.080, width: 0.0080 },
  },
  grip: ionGrip,
  stock: { style: 'bullpup', z: 0.320, y: -0.010, bucket: 'grey' },
  handguard: { style: 'hex', len: 0.190, z: -0.232, r: 0.0280, y: 0.000, bucket: 'grey' },
  barrel: { steps: [[0.0140, -0.400, -0.320], [0.0124, -0.470, -0.400]] },
  muzzle: { style: 'ion', z: -0.470, r: 0.0200, len: 0.110 },
  bipod: { z: -0.318, len: 0.180 },
  magazine: {
    style: 'straight', w: 0.0300, d: 0.0460, len: 0.096, chamfer: 0.0026, ribs: 3,
    offset: [0, -0.040, 0.062], mat: 'grey',
  },
  charging: { style: 'side', side: -1, x: 0.030, y: 0.018, z: -0.064 },
  bolt: { w: 0.030, h: 0.026, len: 0.092, x: 0.002, y: 0.002, z: 0.142 },
  trigger: { y: -0.048, z: -0.104 },
  optic: {
    kind: 'scope', y: 0.0745, z: 0, rTube: 0.0250, rObj: 0.0324, rOc: 0.0258,
    zTip: -0.150, zBack: 0.102, ringZ: [-0.060, 0.040], turretZ: -0.030, reticle: 'christmasTree',
  },
  ejectPoint: [0.029, 0.004, 0.166],
  ejectDir: [0.92, 0.28, 0.24],
  muzzleTip: -0.592,
  extras(K) {
    // Capacitor bank along the left flank, and the emissive charge strip that reads it.
    K.push('grey', place(chamferBox(0.014, 0.040, 0.150, 0.0025), -0.030, -0.006, 0.040));
    K.push('accent', place(plainBox(0.003, 0.008, 0.120), -0.0375, -0.006, 0.040));
    K.push('grey', place(chamferBox(0.014, 0.040, 0.150, 0.0025), 0.030, -0.006, 0.040));
    // Muzzle rails need a standoff bracket back at the barrel shoulder.
    K.metal(place(chamferBox(0.048, 0.014, 0.020, 0.0016), 0, 0.000, -0.404));
  },
  hands: {
    right: { anchor: gripAnchor(ionGrip), curl: 0.98, trigger: 0.72 },
    left: { anchor: [-0.044, -0.034, -0.244], curl: 0.95, spread: 0.32 },
  },
};

/* -------------------------------------------------------------------------- */

/** Small local helpers used only by `extras` blocks above. */
function triggerGuardLoop(zFront, zBack, yTop, yBottom, width = 0.008) {
  const t = 0.006;
  const len = zBack - zFront;
  const drop = yTop - yBottom;
  return mergeGeom([
    place(chamferBox(width, drop * 0.9, t, 0.0012), 0, yTop - drop * 0.45, zFront + 0.004, 0.30, 0, 0),
    place(chamferBox(width, t, len * 0.78, 0.0012), 0, yBottom, zFront + len * 0.5),
    place(chamferBox(width, drop * 0.8, t, 0.0012), 0, yTop - drop * 0.40, zBack - 0.003, -0.35, 0, 0),
  ]);
}

function tubeMag(r, z0, z1, seg = 12) {
  return mergeGeom([
    rodZ(r, z0 + 0.010, z1, seg),
    tubeZ([[r, z0], [r + 0.0030, z0 + 0.008], [r + 0.0030, z0 + 0.016]], seg),
  ]);
}

export const SPECS = {
  ak27,
  x9maverick,
  ds20mirage,
  peacekeepermk1,
  m15mod0,
  mxr17,
  razor9mm,
  mpc25,
  rk9,
  dravec45,
  ryden45k,
  carbon57,
  m10breacher,
  echo12,
  akita,
  xm325,
  mk78,
  warden308,
  m8a1,
  m34novaline,
  vsrecon,
  shadowsk,
  xr3ion,
};
